'use client'
import * as React from 'react'
import { ToolProgress } from '@/components/tools/tool-progress'
import { Search, Loader2, ShieldCheck, AlertTriangle, ExternalLink, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

// Heuristic Shopify theme detector. Runs entirely in the browser via
// a no-cors fetch + a JSONP-ish fallback through allorigins.win. We never
// ship the raw URL to a server; the user's input stays local apart from
// the public CORS proxy. Detection signals are read-only HTML hints any
// page exposes anyway (Shopify.theme global, /admin endpoints, asset URLs).

type Detection = {
  isShopify: boolean
  themeName?: string
  themeRole?: string
  themeId?: number
  themeStoreId?: number
  themeAuthor?: string
  apps: string[]
  ugcTools: string[]
  isIdukkiCustomer: boolean
  isFeefoCustomer: boolean
  signals: string[]
  rawSnippet?: string
}

const KNOWN_THEMES: Record<string, { name: string; author: string; price: string; ugc: string }> = {
  '887': { name: 'Dawn', author: 'Shopify', price: 'Free', ugc: 'Stock UGC blocks. No video carousel out of the box.' },
  '1864': { name: 'Sense', author: 'Shopify', price: 'Free', ugc: 'Limited; pair with shoppable video widgets for PDP video.' },
  '887_dawn': { name: 'Dawn', author: 'Shopify', price: 'Free', ugc: 'Stock UGC blocks.' },
  '1499': { name: 'Impulse', author: 'Archetype Themes', price: '$380', ugc: 'Hero video, no gallery aggregation.' },
  '776': { name: 'Prestige', author: 'Maestrooo', price: '$380', ugc: 'Lookbook native; missing rights mgmt.' },
  '857': { name: 'Symmetry', author: 'Clean Themes', price: '$340', ugc: 'Image-heavy; add hashtag wall + reviews.' },
  '732': { name: 'Streamline', author: 'Out of the Sandbox', price: '$320', ugc: 'Conversion-optimised; bolt on UGC widgets.' },
  '928': { name: 'Motion', author: 'Archetype Themes', price: '$400', ugc: 'Motion-first; pair with shoppable video.' },
  '1864_sense': { name: 'Sense', author: 'Shopify', price: 'Free', ugc: 'Pair with PDP video.' },
}

// Detection order matters. Idukki / Feefo must run first so the customer
// is recognised as ours before we list competitors. `kind` controls the
// pill row in the UI ('idukki' & 'feefo' get the Idukki-customer treatment).
const UGC_TOOL_FINGERPRINTS = [
  // ---- Idukki (the platform itself + Feefo white-label) ----
  {
    match: /data-ugc=["']idukki["']|idukki-cdn\.com|widget-api\.idukki\.io|widget\.idukki\.io|widget-staging\.idukki\.io|app\.idukki\.io|assets\.idukki\.io|idukki\.io\/(?:widget|embed)|cdn\.shopify\.com\/extensions\/[a-f0-9-]+\/[^/]*idukki|shopify-app-id=["'](?:idukki|6\d{12})|\bidukki[._-]?(?:js|widget|loader|runtime)\b/i,
    name: 'Idukki',
    kind: 'idukki', // special-cased in the UI
  },
  { match: /<feefo-ugc|api\.feefo\.com|cdn\.feefo\.com|widget\.feefo\.com/i, name: 'Feefo (Idukki white-label)', kind: 'feefo' },

  // ---- UGC galleries / shoppable video competitors ----
  { match: /bazaarvoice|apps\.bazaarvoice/i, name: 'Bazaarvoice', kind: 'reviews + UGC' },
  { match: /yotpo|yotpo\.com/i, name: 'Yotpo', kind: 'reviews + loyalty' },
  { match: /okendo/i, name: 'Okendo', kind: 'reviews + UGC' },
  { match: /loox\.io|cdn\.loox/i, name: 'Loox', kind: 'photo reviews' },
  { match: /judge\.me/i, name: 'Judge.me', kind: 'reviews' },
  { match: /stamped\.io/i, name: 'Stamped', kind: 'reviews + loyalty' },
  { match: /reviews\.io|cdn\.reviews\.co\.uk/i, name: 'Reviews.io', kind: 'reviews' },
  { match: /trustpilot\.com\/widgets|widget\.trustpilot/i, name: 'Trustpilot', kind: 'reviews' },
  { match: /junip/i, name: 'Junip', kind: 'reviews' },
  { match: /opinew/i, name: 'Opinew', kind: 'reviews' },
  { match: /fera\.ai|frb\.ai/i, name: 'Fera', kind: 'reviews + UGC' },
  { match: /tolstoy/i, name: 'Tolstoy', kind: 'shoppable video' },
  { match: /videowise/i, name: 'Videowise', kind: 'shoppable video' },
  { match: /bambuser/i, name: 'Bambuser', kind: 'live shopping' },
  { match: /firework\.com|fwn\.cc/i, name: 'Firework', kind: 'shoppable video' },
  { match: /vimmi|vidjet/i, name: 'Vidjet', kind: 'shoppable video' },
  { match: /smartzer/i, name: 'Smartzer', kind: 'shoppable video' },
  { match: /quinnvideo|quinn\.live/i, name: 'Quinn', kind: 'shoppable video' },
  { match: /whatmore/i, name: 'Whatmore', kind: 'shoppable video' },
  { match: /getflowbox|flowbox/i, name: 'Flowbox', kind: 'UGC galleries' },
  { match: /storista|stori\.sta/i, name: 'Storista', kind: 'UGC galleries' },
  { match: /embedsocial|embed\.social/i, name: 'EmbedSocial', kind: 'social walls' },
  { match: /pixlee|pixleeturnto/i, name: 'Pixlee TurnTo', kind: 'UGC + reviews' },
  { match: /stackla|nosto-stackla/i, name: 'Stackla', kind: 'UGC galleries' },
  { match: /olapic/i, name: 'Olapic', kind: 'UGC galleries' },
  { match: /\btint\b|tintup/i, name: 'TINT', kind: 'UGC galleries' },
  { match: /candid\.io|candidwm/i, name: 'Candid', kind: 'UGC + reviews' },
  { match: /publitas/i, name: 'Publitas', kind: 'shoppable catalogs' },

  // ---- Adjacent stack (kept so the audit reads as a full UGC stack) ----
  { match: /nostojs|nosto\.com/i, name: 'Nosto', kind: 'personalization' },
  { match: /klaviyo/i, name: 'Klaviyo', kind: 'email + SMS' },
  { match: /attentive/i, name: 'Attentive', kind: 'SMS' },
  { match: /gorgias/i, name: 'Gorgias', kind: 'support desk' },
  { match: /rebuy/i, name: 'Rebuy', kind: 'upsells' },
  { match: /shogun/i, name: 'Shogun', kind: 'page builder' },
  { match: /tapcart/i, name: 'Tapcart', kind: 'mobile app' },
  { match: /postscript/i, name: 'Postscript', kind: 'SMS' },
  { match: /bitespeed/i, name: 'BiteSpeed', kind: 'WhatsApp marketing' },
]

async function fetchViaProxy(url: string): Promise<string> {
  // Try the in-house server proxy first, most reliable, no third-party.
  // If that fails (Vercel outbound block, function crash) fall through to
  // public CORS proxies. We try each in sequence until one returns valid
  // HTML; only after all four fail do we throw.
  const sources: Array<() => Promise<string>> = [
    // 1. Idukki server proxy (Edge runtime, same-origin)
    async () => {
      const r = await fetch(`/api/proxy-fetch?url=${encodeURIComponent(url)}`, {
        signal: AbortSignal.timeout(15000),
      })
      const j = await r.json()
      if (!r.ok || typeof j.contents !== 'string') throw new Error(j.error || 'proxy-fetch failed')
      return j.contents
    },
    // 2. corsproxy.io, returns raw HTML, no JSON envelope
    async () => {
      const r = await fetch(`https://corsproxy.io/?${encodeURIComponent(url)}`, {
        signal: AbortSignal.timeout(15000),
      })
      if (!r.ok) throw new Error('corsproxy.io returned ' + r.status)
      return await r.text()
    },
    // 3. allorigins.win. JSON envelope, the previous default
    async () => {
      const r = await fetch(
        `https://api.allorigins.win/get?url=${encodeURIComponent(url)}&nocache=${Date.now()}`,
        { signal: AbortSignal.timeout(15000) },
      )
      if (!r.ok) throw new Error('allorigins returned ' + r.status)
      const j = await r.json()
      if (typeof j.contents !== 'string') throw new Error('allorigins envelope missing')
      return j.contents
    },
    // 4. codetabs proxy
    async () => {
      const r = await fetch(`https://api.codetabs.com/v1/proxy/?quest=${encodeURIComponent(url)}`, {
        signal: AbortSignal.timeout(15000),
      })
      if (!r.ok) throw new Error('codetabs returned ' + r.status)
      return await r.text()
    },
  ]

  const errs: string[] = []
  for (const src of sources) {
    try {
      const html = await src()
      if (html && html.length > 100) return html
      errs.push('empty body')
    } catch (e) {
      errs.push(e instanceof Error ? e.message : String(e))
    }
  }
  throw new Error(`All proxies failed, ${errs.join(' · ')}`)
}

function normalizeUrl(input: string) {
  const trimmed = input.trim()
  if (!trimmed) return ''
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  return 'https://' + trimmed.replace(/^\/+/, '')
}

// Many UGC widgets (Idukki, Loox, Yotpo, etc.) only mount on PDPs, the
// homepage alone hides them. We try to pull the first /products/{handle}
// link out of the homepage HTML so we can fetch a representative PDP and
// concatenate both responses before running detection.
function extractFirstProductUrl(homepageUrl: string, html: string): string | null {
  const productPath = html.match(/href=["'](\/products\/[a-z0-9][a-z0-9-]*)(?:[?#"'][^"']*)?["']/i)?.[1]
  if (!productPath) return null
  try {
    const u = new URL(homepageUrl)
    return `${u.origin}${productPath}`
  } catch {
    return null
  }
}

function detect(html: string): Detection {
  const signals: string[] = []
  const apps = new Set<string>()
  const ugcTools = new Set<string>()
  let themeName: string | undefined
  let themeRole: string | undefined
  let themeId: number | undefined
  let themeStoreId: number | undefined
  let themeAuthor: string | undefined

  const isShopify =
    /cdn\.shopify\.com/i.test(html) ||
    /Shopify\.theme\s*=/.test(html) ||
    /shopify-features/i.test(html) ||
    /myshopify\.com/i.test(html)

  if (!isShopify) {
    return { isShopify: false, apps: [], ugcTools: [], isIdukkiCustomer: false, isFeefoCustomer: false, signals: ['No Shopify fingerprints found on this URL.'] }
  }

  const themeBlock = html.match(/Shopify\.theme\s*=\s*({[\s\S]*?});/)
  if (themeBlock) {
    signals.push('Shopify.theme global is exposed in the page source.')
    const tName = themeBlock[1].match(/"name"\s*:\s*"([^"]+)"/)?.[1]
    const tRole = themeBlock[1].match(/"role"\s*:\s*"([^"]+)"/)?.[1]
    const tId = themeBlock[1].match(/"id"\s*:\s*(\d+)/)?.[1]
    const tStore = themeBlock[1].match(/"theme_store_id"\s*:\s*(\d+)/)?.[1]
    if (tName) themeName = tName
    if (tRole) themeRole = tRole
    if (tId) themeId = Number(tId)
    if (tStore) themeStoreId = Number(tStore)
  }

  if (themeStoreId && KNOWN_THEMES[String(themeStoreId)]) {
    const k = KNOWN_THEMES[String(themeStoreId)]
    if (!themeName) themeName = k.name
    themeAuthor = k.author
    signals.push(`Resolved theme_store_id ${themeStoreId} → ${k.name} by ${k.author} (${k.price}).`)
  }

  const cdnAuthor = html.match(/cdn\.shopify\.com\/s\/files\/.*?themes\/([^\/]+)\//i)
  if (cdnAuthor) {
    themeAuthor = themeAuthor || decodeURIComponent(cdnAuthor[1]).replace(/-/g, ' ')
    signals.push('Found theme asset path in CDN URLs.')
  }

  const meta = html.match(/<meta\s+name=["']generator["']\s+content=["']([^"']+)["']/i)
  if (meta) signals.push(`Generator meta: ${meta[1]}`)

  let isIdukkiCustomer = false
  let isFeefoCustomer = false
  for (const fp of UGC_TOOL_FINGERPRINTS) {
    if (fp.match.test(html)) {
      if (fp.kind === 'idukki') { isIdukkiCustomer = true; ugcTools.add(`${fp.name}. UGC + shoppable video`); continue }
      if (fp.kind === 'feefo')  { isFeefoCustomer = true;  ugcTools.add(`${fp.name}`); continue }
      ugcTools.add(`${fp.name}, ${fp.kind}`)
    }
  }
  if (isIdukkiCustomer) signals.push('Idukki widget detected, this is already an Idukki customer.')
  if (isFeefoCustomer)  signals.push('Feefo UGC detected, this is Idukki white-labelled as Feefo.')

  const appMatches = html.matchAll(/cdn\.shopify\.com\/extensions\/[^\/]+\/([^\/]+)\//gi)
  for (const m of appMatches) apps.add(m[1])
  const blockMatches = html.matchAll(/data-app-block-handle=["']([^"']+)["']/gi)
  for (const m of blockMatches) apps.add(m[1])

  if (html.includes('checkout.shopify.com') || html.includes('/cart/add.js')) {
    signals.push('Cart API + Shopify checkout endpoints found.')
  }

  const rawSnippet = themeBlock?.[0]?.slice(0, 400)

  return {
    isShopify: true,
    themeName,
    themeRole,
    themeId,
    themeStoreId,
    themeAuthor,
    apps: Array.from(apps).slice(0, 12),
    ugcTools: Array.from(ugcTools),
    isIdukkiCustomer,
    isFeefoCustomer,
    signals,
    rawSnippet,
  }
}

export function ShopifyThemeDetector() {
  const [url, setUrl] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [result, setResult] = React.useState<Detection | null>(null)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setResult(null)
    const target = normalizeUrl(url)
    if (!target) {
      setError('Enter a store URL, for example glossier.com or https://allbirds.com.')
      return
    }
    setLoading(true)
    try {
      const homepage = await fetchViaProxy(target)
      // Idukki + most UGC widgets only mount on PDPs. Fetch one PDP too and
      // join the HTML so detection covers both surfaces. PDP fetch is
      // best-effort, if it fails we still return homepage-only detection.
      let combined = homepage
      let pdpUrl: string | null = null
      try {
        pdpUrl = extractFirstProductUrl(target, homepage)
        if (pdpUrl) {
          const pdpHtml = await fetchViaProxy(pdpUrl)
          combined = homepage + '\n<!-- PDP -->\n' + pdpHtml
        }
      } catch {
        /* PDP failure is non-fatal, keep homepage-only detection */
      }
      const detection = detect(combined)
      if (pdpUrl) detection.signals.push(`Scanned homepage + PDP (${new URL(pdpUrl).pathname}).`)
      setResult(detection)
      if (!detection.isShopify) {
        setError(`Couldn't find Shopify fingerprints on ${target}. The store may be on BigCommerce, Wix, Magento or behind a paywall.`)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch, the store may block scraping or the proxy timed out.')
    } finally {
      setLoading(false)
    }
  }

  const knownTheme = result?.themeStoreId ? KNOWN_THEMES[String(result.themeStoreId)] : undefined
  const ugcTool = knownTheme?.ugc

  return (
    <div className="rounded-2xl border bg-card shadow-sm">
      <form onSubmit={onSubmit} className="flex flex-col gap-3 border-b p-5 sm:flex-row sm:items-end sm:p-6">
        <label className="flex-1">
          <span className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">Shopify store URL</span>
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="glossier.com"
            disabled={loading}
            className="mt-1.5"
          />
        </label>
        <Button type="submit" disabled={loading} className="sm:w-44">
          {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Detecting</> : <><Search className="mr-2 h-4 w-4" /> Detect theme</>}
        </Button>
      </form>

      {loading && (
        <ToolProgress label="the store" steps={[{ title: "Fetching the store", sub: "Loading the storefront" }, { title: "Detecting theme + apps", sub: "Theme fingerprint and app signatures" }, { title: "Compiling the report", sub: "Theme, apps and stack" }]} />
      )}

      {error && (
        <div className="flex items-start gap-3 border-b bg-amber-50 px-5 py-4 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200 sm:px-6">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {result?.isShopify && (
        <div className="grid gap-4 p-5 sm:p-6 md:grid-cols-2 md:gap-6">
          <ResultCard
            label="Theme"
            primary={result.themeName || 'Custom / unknown'}
            secondary={[
              result.themeAuthor && `by ${result.themeAuthor}`,
              knownTheme && `${knownTheme.price}`,
              result.themeRole && `role: ${result.themeRole}`,
              result.themeStoreId && `theme_store_id: ${result.themeStoreId}`,
            ].filter(Boolean).join(' · ')}
            icon={<ShieldCheck className="h-4 w-4 text-emerald-600" />}
          />
          <ResultCard
            label={result.isIdukkiCustomer || result.isFeefoCustomer ? 'UGC stack. Idukki detected' : 'UGC stack detected'}
            primary={result.ugcTools.length ? `${result.ugcTools.length} UGC ${result.ugcTools.length === 1 ? 'app' : 'apps'}` : 'No UGC stack found'}
            secondary={result.ugcTools.length ? result.ugcTools.join(' · ') : 'Looks like a clean install, perfect time to consolidate on Idukki.'}
            icon={<Sparkles className="h-4 w-4 text-primary" />}
            accent
          />
          {(result.isIdukkiCustomer || result.isFeefoCustomer) ? (
            <div className="md:col-span-2 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-200">
              <strong className="font-medium">{result.isFeefoCustomer ? 'Feefo (Idukki white-label) detected.' : 'Idukki widget detected on this store.'}</strong>{' '}
              You&rsquo;re already running Idukki. To get more from it, layer in shoppable video on the PDP, a hashtag wall on the homepage, and Klaviyo flows on UGC events.
            </div>
          ) : ugcTool && (
            <div className="md:col-span-2 rounded-xl border bg-primary/5 p-4 text-sm text-primary">
              <strong className="font-medium">Idukki tip:</strong> {ugcTool} Add a shoppable video carousel + hashtag wall and you'll close the gap on conversion-optimised themes.
            </div>
          )}
          {result.apps.length > 0 && (
            <div className="md:col-span-2 rounded-xl border bg-card p-4">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Theme extensions + app blocks</div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {result.apps.map((a) => (
                  <span key={a} className="rounded-full border bg-secondary px-2 py-0.5 text-xs">{a}</span>
                ))}
              </div>
            </div>
          )}
          {result.signals.length > 0 && (
            <div className="md:col-span-2 rounded-xl border bg-card p-4">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Signals</div>
              <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                {result.signals.map((s) => <li key={s}>· {s}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col gap-3 border-t bg-secondary/30 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
        <p className="text-xs text-muted-foreground sm:text-sm">
          Fetched through a public CORS proxy. We don&rsquo;t log URLs or store any results.
        </p>
        <Button asChild size="sm" variant="outline">
          <a href="https://apps.shopify.com/idukki" target="_blank" rel="noopener noreferrer">
            Idukki on Shopify <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
          </a>
        </Button>
      </div>
    </div>
  )
}

function ResultCard({ label, primary, secondary, icon, accent }: { label: string; primary: string; secondary?: string; icon?: React.ReactNode; accent?: boolean }) {
  return (
    <div className={cn('rounded-xl border p-4', accent && 'border-primary/40 bg-primary/5')}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="mt-1.5 font-display text-xl font-medium">{primary}</div>
      {secondary && <div className="mt-1 text-xs text-muted-foreground">{secondary}</div>}
    </div>
  )
}
